<!-- ABOUTME: Broadcast emails list component with responsive flex layout -->
<!-- ABOUTME: Modern design using Tailwind CSS utility classes and Lucide Vue icons with dropdown menu for actions -->
<template>
  <div class="min-h-screen">
    <!-- Header Section -->
    <header class="py-5 px-5">
      <div class="max-w-7xl">
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 class="text-3xl font-bold text-title-dark m-0">Broadcast Emails</h1>
              <p class="text-text-dark text-sm mt-1 mb-0">Send transactional emails to selected users</p>
            </div>

            <div class="flex flex-wrap gap-2">
              <a
                :href="newEmailPath"
                class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md no-underline font-medium text-sm transition-opacity hover:opacity-90">
                <Plus :size="16" class="mr-2" />
                Create New Email
              </a>

              <button
                @click="toggleArchived"
                class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-text-dark rounded-md font-medium text-sm cursor-pointer transition-colors hover:bg-gray-50">
                <Archive :size="16" class="mr-2" />
                {{ showArchived ? 'Show Active' : 'Show Archived' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl py-6 px-5">

      <!-- Emails List -->
      <div v-if="emails.length > 0" class="flex flex-col gap-2">
        <div
          v-for="email in emails"
          :key="email.id"
          class="bg-white rounded-md border border-gray-200 py-2.5 px-4 transition-colors hover:border-primary">

          <!-- Single Row Layout -->
          <div class="flex items-center justify-between gap-4 flex-wrap">

            <!-- Left: Title & Meta -->
            <div class="flex items-center gap-4 flex-1 min-w-0">
              <!-- Clickable Title -->
              <a
                :href="email.status === 'draft' ? getEditPath(email.id) : getViewPath(email.id)"
                class="text-[15px] font-semibold text-title-dark m-0 overflow-hidden text-ellipsis whitespace-nowrap no-underline hover:text-primary transition-colors cursor-pointer">
                {{ email.title || 'Untitled' }}
              </a>

              <!-- Date -->
              <span class="text-gray-400 text-[13px] flex-shrink-0 hidden sm:inline">
                {{ formatDate(email.created_at) }}
              </span>

              <!-- Languages -->
              <div class="hidden md:flex items-center gap-1.5">
                <Globe :size="14" :stroke-width="2" class="text-gray-400" />
                <span class="text-text-dark text-[13px]">
                  {{ email.languages.length > 0 ? email.languages.join(', ') : 'No variants' }}
                </span>
              </div>

              <!-- Status Badge -->
              <span
                :class="[getStatusClass(email.status), 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0']">
                {{ getStatusText(email.status) }}
              </span>
            </div>

            <!-- Right: Actions Dropdown -->
            <div class="relative flex-shrink-0">
              <button
                @click="toggleDropdown(email.id)"
                class="inline-flex items-center justify-center w-8 h-8 text-text-dark rounded-md cursor-pointer transition-colors hover:bg-gray-100 border-0 bg-transparent">
                <MoreVertical :size="18" :stroke-width="2" />
              </button>

              <!-- Dropdown Menu -->
              <div
                v-if="activeDropdown === email.id"
                class="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div class="py-1">
                  <a
                    :href="getViewPath(email.id)"
                    class="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50 no-underline transition-colors">
                    <Eye :size="14" :stroke-width="2" class="mr-2" />
                    View
                  </a>

                  <a
                    v-if="email.status === 'draft'"
                    :href="getEditPath(email.id)"
                    class="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50 no-underline transition-colors">
                    <Pencil :size="14" :stroke-width="2" class="mr-2" />
                    Edit
                  </a>

                  <button
                    v-if="email.status === 'draft'"
                    @click="confirmDelete(email.id, email.title)"
                    class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-0 bg-transparent text-left cursor-pointer transition-colors">
                    <Trash2 :size="14" :stroke-width="2" class="mr-2" />
                    Delete
                  </button>

                  <button
                    v-if="email.status !== 'archived'"
                    @click="confirmArchive(email.id, email.title)"
                    class="flex items-center w-full px-4 py-2 text-sm text-text-dark hover:bg-gray-50 border-0 bg-transparent text-left cursor-pointer transition-colors">
                    <Archive :size="14" :stroke-width="2" class="mr-2" />
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Mail :size="64" :stroke-width="1.5" class="text-gray-300 mx-auto mb-4 block" />
        <p class="text-gray-500 text-base">No broadcast emails sent yet.</p>
      </div>
    </main>
  </div>
</template>

<script>
import { Plus, Archive, Globe, User, Eye, Pencil, Trash2, Mail, MoreVertical } from 'lucide-vue-next'

export default {
  name: 'BroadcastEmailsList',

  components: {
    Plus,
    Archive,
    Globe,
    User,
    Eye,
    Pencil,
    Trash2,
    Mail,
    MoreVertical
  },

  props: {
    emails: {
      type: Array,
      required: true
    },
    showArchived: {
      type: Boolean,
      default: false
    },
    indexPath: {
      type: String,
      required: true
    },
    newEmailPath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      activeDropdown: null
    }
  },

  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    toggleDropdown(emailId) {
      this.activeDropdown = this.activeDropdown === emailId ? null : emailId
    },

    handleClickOutside(event) {
      // Close dropdown if clicking outside
      if (!event.target.closest('.relative')) {
        this.activeDropdown = null
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusClass(status) {
      const classes = {
        sent: 'bg-green-100 text-green-800',
        draft: 'bg-amber-100 text-amber-800',
        archived: 'bg-gray-100 text-gray-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-700'
    },

    getStatusText(status) {
      const texts = {
        sent: '✓ Sent',
        draft: 'Draft',
        archived: 'Archived'
      }
      return texts[status] || status.charAt(0).toUpperCase() + status.slice(1)
    },

    getViewPath(id) {
      return `${this.indexPath}/${id}`
    },

    getEditPath(id) {
      return `${this.indexPath}/${id}/edit`
    },

    toggleArchived() {
      const url = this.showArchived
        ? this.indexPath
        : `${this.indexPath}?show_archived=true`
      window.location.href = url
    },

    confirmDelete(id, title) {
      if (confirm(`Delete this draft: "${title || 'Untitled'}"?`)) {
        this.deleteEmail(id)
      }
    },

    deleteEmail(id) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${this.indexPath}/${id}`

      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = 'delete'

      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken

      form.appendChild(methodInput)
      form.appendChild(tokenInput)
      document.body.appendChild(form)
      form.submit()
    },

    confirmArchive(id, title) {
      if (confirm(`Archive this broadcast: "${title || 'Untitled'}"?`)) {
        this.archiveEmail(id)
      }
    },

    archiveEmail(id) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${this.indexPath}/${id}/archive`

      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken

      form.appendChild(tokenInput)
      document.body.appendChild(form)
      form.submit()
    }
  }
}
</script>
